import React, { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const emptyQ = () => ({ question: "", options: ["", "", "", ""], answer_index: 0, explanation: "" });

export const LessonEditorDialog = ({ open, onOpenChange, courseId, onSaved }) => {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [objectives, setObjectives] = useState("");
  const [sections, setSections] = useState([{ heading: "", body: "" }]);
  const [vocab, setVocab] = useState([]);
  const [quiz, setQuiz] = useState([emptyQ()]);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setTitle(""); setSummary(""); setObjectives(""); setSections([{ heading: "", body: "" }]); setVocab([]); setQuiz([emptyQ()]);
  };

  const save = async () => {
    const secs = sections.filter((s) => s.heading.trim() || s.body.trim());
    if (!title.trim() || secs.length === 0) return toast.error("Add a title and at least one section");
    const qs = quiz.filter((q) => q.question.trim()).map((q) => ({ ...q, options: q.options.map((o) => o.trim()).filter(Boolean) }));
    for (const q of qs) {
      if (q.options.length < 2) return toast.error(`"${q.question.slice(0, 30)}" needs at least 2 options`);
      if (q.answer_index >= q.options.length) return toast.error(`Pick a valid correct answer for "${q.question.slice(0, 30)}"`);
    }
    setBusy(true);
    try {
      await api.post(`/courses/${courseId}/lessons`, {
        title, summary,
        objectives: objectives.split("\n").map((o) => o.trim()).filter(Boolean),
        sections: secs,
        vocabulary: vocab.filter((v) => v.term.trim()),
        quiz: qs,
      });
      toast.success("Lesson added");
      reset();
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const upd = (arr, setArr, i, patch) => setArr(arr.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" data-testid="lesson-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Write a lesson</DialogTitle>
          <DialogDescription>Add your own content, vocabulary and a multiple-choice quiz.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="editor-title-input" /></div>
          <div className="space-y-2"><Label>Summary</Label><Input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="One or two sentences" data-testid="editor-summary-input" /></div>
          <div className="space-y-2"><Label>Learning objectives (one per line)</Label><Textarea rows={3} value={objectives} onChange={(e) => setObjectives(e.target.value)} data-testid="editor-objectives-input" /></div>

          <div className="space-y-3">
            <div className="flex items-center justify-between"><Label>Sections</Label><Button size="sm" variant="outline" onClick={() => setSections([...sections, { heading: "", body: "" }])} data-testid="editor-add-section"><Plus className="h-3.5 w-3.5 mr-1" />Section</Button></div>
            {sections.map((s, i) => (
              <div key={i} className="border border-border rounded-xl p-3 space-y-2 bg-[#FBFAF4]">
                <div className="flex gap-2">
                  <Input placeholder="Heading" value={s.heading} onChange={(e) => upd(sections, setSections, i, { heading: e.target.value })} className="bg-white" data-testid="editor-section-heading" />
                  {sections.length > 1 && <Button variant="ghost" size="icon" onClick={() => setSections(sections.filter((_, j) => j !== i))} aria-label="Remove section"><Trash2 className="h-4 w-4" /></Button>}
                </div>
                <Textarea placeholder="Content (blank lines start new paragraphs)" rows={4} value={s.body} onChange={(e) => upd(sections, setSections, i, { body: e.target.value })} className="bg-white" data-testid="editor-section-body" />
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between"><Label>Vocabulary</Label><Button size="sm" variant="outline" onClick={() => setVocab([...vocab, { term: "", definition: "" }])} data-testid="editor-add-vocab"><Plus className="h-3.5 w-3.5 mr-1" />Term</Button></div>
            {vocab.map((v, i) => (
              <div key={i} className="flex gap-2">
                <Input placeholder="Term" value={v.term} onChange={(e) => upd(vocab, setVocab, i, { term: e.target.value })} className="w-1/3" />
                <Input placeholder="Definition" value={v.definition} onChange={(e) => upd(vocab, setVocab, i, { definition: e.target.value })} />
                <Button variant="ghost" size="icon" onClick={() => setVocab(vocab.filter((_, j) => j !== i))} aria-label="Remove term"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between"><Label>Quiz questions</Label><Button size="sm" variant="outline" onClick={() => setQuiz([...quiz, emptyQ()])} data-testid="editor-add-question"><Plus className="h-3.5 w-3.5 mr-1" />Question</Button></div>
            {quiz.map((q, i) => (
              <div key={i} className="border border-border rounded-xl p-3 space-y-2 bg-[#FBFAF4]">
                <div className="flex gap-2">
                  <Input placeholder={`Question ${i + 1}`} value={q.question} onChange={(e) => upd(quiz, setQuiz, i, { question: e.target.value })} className="bg-white" data-testid="editor-question-input" />
                  <Button variant="ghost" size="icon" onClick={() => setQuiz(quiz.filter((_, j) => j !== i))} aria-label="Remove question"><Trash2 className="h-4 w-4" /></Button>
                </div>
                {q.options.map((o, k) => (
                  <div key={k} className="flex items-center gap-2">
                    <input type="radio" name={`ans-${i}`} checked={q.answer_index === k} onChange={() => upd(quiz, setQuiz, i, { answer_index: k })} aria-label={`Mark option ${k + 1} correct`} className="accent-[#0F7B7D] h-4 w-4" data-testid="editor-correct-radio" />
                    <Input placeholder={`Option ${k + 1}`} value={o} onChange={(e) => upd(quiz, setQuiz, i, { options: q.options.map((x, m) => (m === k ? e.target.value : x)) })} className="bg-white" data-testid="editor-option-input" />
                  </div>
                ))}
                <Input placeholder="Explanation shown after answering (optional)" value={q.explanation} onChange={(e) => upd(quiz, setQuiz, i, { explanation: e.target.value })} className="bg-white" />
                <p className="text-xs text-muted-foreground">Select the circle next to the correct answer.</p>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="editor-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save lesson</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
